import { CombineRule, CombineRuleSearch, SearchCombineRuleResponseCombineRuleConnectionArgs } from '@/models/gen/graphql';
import {
  ConnectionDetails,
  HandleErrorInput,
  convertConnectionToDetails,
  handleError,
  mergeConnectionDetails as merge,
} from '@/utils/custom';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { SearchCombineRulesDocument } from '@/api/queries';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Global Service Variables
const mergeKey = 'searchCombineRules.combineRuleTableConnection';
const onError = (res: HandleErrorInput): void => handleError(res, { notification: { title: 'Search Combine Rules' } });

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof CombineRuleSearch]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<CombineRule>>;
}

// SearchBufferTimes Service
export const { searchCombineRules, refetchCombineRule, useSearchCombineRules } = ((): {
  searchCombineRules: ServiceMethod;
  refetchCombineRule: ServiceMethod;
  useSearchCombineRules: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch, refetch] = graphApi(SearchCombineRulesDocument, {
    onError,
  }).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<ConnectionDetails<CombineRule>> => {
        const emptyPayload = payload.every((value) => {
          return Object.values(value).every((v) => v === null);
        });
        const args: SearchCombineRuleResponseCombineRuleConnectionArgs = {
          input: {
            first: options?.pageSize || null,
            after: options?.pageSize > 0 ? (options?.pageSize * options?.page).toString() : null,
            query: !emptyPayload ? payload : [],
          },
        };
        const opts = { merge: options?.merge ? mergeKey : undefined };
        const res = await fn(args, opts);
        return convertConnectionToDetails(getProperty(mergeKey, res));
      }
  );
  const useService = createGraphApiHook(fetch, { refetch, merge });
  return {
    searchCombineRules: fetch,
    refetchCombineRule: refetch,
    useSearchCombineRules: useService,
  };
})();
